import { useEffect, useRef, useState } from 'react'
import { Dialog } from '../Dialog'
import { TraySection, useReportDirty } from './TraySection'
import { useShopImage } from '../../lib/useShopImage'
import { RowToggle } from '../wiki/RowToggle'
import { Picker } from '../Picker'
import { PickerFoot } from '../PickerFoot'
import { messageOf } from '../../lib/errors'
import { loadOrgUnits, type OrgUnitOption } from '../../lib/admin'
import { searchMentionable, type MentionablePerson } from '../../lib/mentions'
import { AxisEditor, axesReady } from './AxisEditor'
import { ItemPictures } from './ItemPictures'
import { ItemDemand, ItemPledges } from './PreOrder'
import {
  addItemAudience,
  codeProblem,
  createShopItem,
  editShopItem,
  removeItemAudience,
  setItemActive,
  setItemCatalogue,
  setItemOrdering,
  setItemPrice,
  slugifyCode,
  type AxisDraft,
  type ManagedItem,
  type StockRoom,
} from '../../lib/shop'
import {
  ALL_SECTIONS,
  ITEM_FILTERS,
  SECTIONS,
  audienceSummary,
  defaultOpenSections,
  detailsSummary,
  filterItems,
  optionsSummary,
  orderingSummary,
  picturesSummary,
  pledgesSummary,
  statusChips,
  stockByStore,
  stockSummary,
  stockTotals,
  type ItemFilter,
  type SectionKey,
} from '../../lib/shopAdmin'

/** The stored shape, as the editor wants it. */
function toDrafts(item: ManagedItem): AxisDraft[] {
  return [...item.axes]
    .sort((a, b) => a.position - b.position)
    .map((a) => ({ kind: a.kind, label: a.label, options: a.options.map((o) => o.label) }))
}

/**
 * The catalogue, from the manager's side. REC-9, REC-24, and the screen REC-10
 * was missing.
 *
 * ── THIS TAB IS THE CATALOGUE. COUNTING HAPPENS ON THE STOCK TAB (074) ──────
 *
 * David, 15 Sep: "Separate create an item and add item stock." Editing what an
 * item IS — its price, what it comes in, its pictures, who it is for, how it
 * may be ordered — is a catalogue decision taken once. Counting a shelf happens
 * every time a delivery arrives, by somebody standing in the room. They were
 * the same screen until now, which is why stock could only be typed from inside
 * an expanded item and why a person in Burnaby had to open every item in the
 * catalogue to count their own shelves.
 *
 * So the table below is READ-ONLY and says where to change it. Two surfaces
 * that both write one number is how a badge starts lying (029) — and here they
 * would be racing over a shelf rather than a count.
 *
 * The room FILTER went with it, deliberately. It was this screen trying to be
 * the room view and doing it badly: a per-room narrowing of an item-first read.
 * The Stock tab is that view now, done properly, from a read that starts at the
 * room. Leaving the filter here would be two answers to "what is on the Burnaby
 * shelf", which is the thing the split exists to end.
 *
 * ── THREE NUMBERS, AND ONLY ONE OF THEM IS EVER TYPED ───────────────────────
 *
 * On hand is what somebody counted. Held is what is promised to an order and is
 * written only by the order path — nobody types it anywhere. Free is the
 * subtraction, computed in the database so there is one definition of it.
 *
 * ── NO CATEGORY THRESHOLD (128) ─────────────────────────────────────────────
 *
 * REC-24's "unlocked by stars earned under a category" was REMOVED with its
 * columns (David, 30 Sep: "remove the category requirements from store all
 * together and we can put endorsements back in later"). The later shape is a
 * count of endorsements, which is a different control; nothing here is kept
 * half-alive for it.
 */
export function ShopItems({
  items,
  rooms,
  tenantId,
  canAdmin = false,
  openItemId = null,
  onOpenItem,
  openSections,
  onChanged,
}: {
  items: ManagedItem[]
  rooms: StockRoom[]
  /** Needed for the image path — `<tenant>/<item>/…` is what 070's policy reads. */
  tenantId: string
  /**
   * shop.admin. Only it may change WHICH STORE PRICES an item (065/076) — a
   * room manager runs a shelf, which is a different act (021). Presentation:
   * `edit_shop_item` re-checks it.
   */
  canAdmin?: boolean
  /**
   * Which item's tray is open. CONTROLLED by the route — `#/admin/shop/items/:id`
   * since REC-65, so Back closes the tray and a link opens it.
   *
   * It also means the tray can be rendered by the test suite at all. A
   * disclosure that only ever opens on a click is a panel no assertion can
   * reach, and every check written against it passes whether or not the
   * contents are right — which is the same blindness as a suite running as the
   * database owner.
   */
  openItemId?: string | null
  onOpenItem?: (itemId: string | null) => void
  /**
   * Which sections of the open tray start expanded. Omitted, the editor's own
   * rule applies (all collapsed unless one needs action — David, 4 Oct). The
   * suite passes 'all'; the route passes ['options'] for a just-created item.
   */
  openSections?: SectionKey[] | 'all'
  onChanged: () => void
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<ItemFilter>('all')

  if (items.length === 0) {
    return (
      <p className="empty">
        Nothing in the shop yet. Add an item, give it at least one variant —
        even a mug needs one, because stock hangs off the variant and never off
        the item — then count what is on the shelf.
      </p>
    )
  }

  const shown = filterItems(items, query, filter)
  const open = openItemId ? items.find((i) => i.itemId === openItemId) ?? null : null
  // A search and four chips earn their space on a catalogue, not on three
  // rows — and below this a filter is something to clear rather than a help.
  const searchable = items.length > FILTER_FROM

  return (
    <div className="shopitems">
      {searchable && (
        <div className="shopitems__filters">
          <label className="admin__field shopitems__search">
            <span className="sr-only">Find an item</span>
            <input
              type="search"
              value={query}
              placeholder="Find an item by name or code"
              autoComplete="off"
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div className="chips" role="group" aria-label="Show">
            {ITEM_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`chip chip--sm${filter === f.key ? ' chip--on' : ''}`}
                aria-pressed={filter === f.key}
                onClick={() => setFilter(f.key)}
              >
                {f.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <p className="empty">
          Nothing matches. {items.length} {items.length === 1 ? 'item is' : 'items are'} in the
          catalogue —{' '}
          <button type="button" className="btn btn--link" onClick={() => { setQuery(''); setFilter('all') }}>
            show them all
          </button>
          .
        </p>
      ) : (
        <ul className="shopitems__list">
          {shown.map((item) => (
            <ItemRow
              key={item.itemId}
              item={item}
              open={openItemId === item.itemId}
              onOpen={() => onOpenItem?.(item.itemId)}
            />
          ))}
        </ul>
      )}

      {open && (
        <ItemTray
          key={open.itemId}
          item={open}
          rooms={rooms}
          tenantId={tenantId}
          canAdmin={canAdmin}
          openSections={openSections}
          onClose={() => onOpenItem?.(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  )
}

/** Below this many items the list has no search box or filter chips. */
export const FILTER_FROM = 5

/**
 * ONE DENSE ROW PER ITEM (REC-65). Picture, name, price, free/held, and the
 * chips a manager scans for — pre-order state first. The whole row is one
 * button that opens the tray; nothing inside it is a second control, so there
 * is no nested-interactive trap and one press always means "open this".
 */
function ItemRow({ item, open, onOpen }: { item: ManagedItem; open: boolean; onOpen: () => void }) {
  const thumb = useShopImage(item.image?.path ?? null)
  const t = stockTotals(item)
  return (
    <li className={`shopitems__item${item.isActive ? '' : ' is-delisted'}${open ? ' is-open' : ''}`}>
      <button type="button" className="shopitems__row" aria-haspopup="dialog" onClick={onOpen}>
        <span className="shopitems__thumb" aria-hidden="true">
          {thumb ? <img src={thumb} alt="" loading="lazy" /> : <span>{item.name.charAt(0)}</span>}
        </span>
        <span className="shopitems__name">
          <span className="shopitems__title">{item.name}</span>
          <span className="shopitems__code">{item.code}</span>
        </span>
        {/* The price reads first and in stars — there is no cash anywhere in
            this app (D13), and the 1c planning guide is not stored, served or
            rendered. */}
        <span className="pill pill--star">{item.priceStars.toLocaleString('en-CA')} ★</span>
        <span className="shopitems__stock">
          {t.free} free
          {t.held > 0 && <span className="shopitems__held"> · {t.held} held</span>}
        </span>
        <span className="shopitems__facts">
          {statusChips(item).map((c) => (
            <span key={c.text} className={`tag shopitems__chip shopitems__chip--${c.tone}`}>{c.text}</span>
          ))}
          {item.ownerRoomId ? (
            <span className="tag" title="Priced by the room that owns it">
              Local: {item.ownerRoomName ?? 'one store'}
            </span>
          ) : (
            <span className="tag" title="Priced company-wide by a shop administrator">
              Group
            </span>
          )}
        </span>
      </button>
    </li>
  )
}

/**
 * THE ITEM, IN A TRAY OVER THE LIST (REC-65; David chose the tray over a page,
 * 4 Oct). It IS a `Dialog` — focus in, Tab trapped, Esc closes, focus back to
 * the row — with a right-hand layout, rather than a second surface that would
 * have to relearn what the bell panel got wrong.
 *
 * CLOSING WITH UNSAVED EDITS ASKS FIRST, in the tray rather than in a native
 * confirm: each section keeps its own Save (049 — one "save all" would mix
 * REPLACE calls that each send their whole state), so an edit can sit unsaved
 * in a collapsed section, and closing the tray would lose it silently.
 */
function ItemTray({
  item,
  rooms,
  tenantId,
  canAdmin,
  openSections,
  onClose,
  onChanged,
}: {
  item: ManagedItem
  rooms: StockRoom[]
  tenantId: string
  canAdmin: boolean
  openSections?: SectionKey[] | 'all'
  onClose: () => void
  onChanged: () => void
}) {
  const [dirty, setDirty] = useState<SectionKey[]>([])
  const [asking, setAsking] = useState(false)

  function requestClose() {
    if (dirty.length > 0 && !asking) {
      setAsking(true)
      return
    }
    onClose()
  }

  return (
    <Dialog open title={item.name} variant="tray" onClose={requestClose}>
      {asking && dirty.length > 0 && (
        <div className="tray__confirm" role="alert">
          <p>
            Unsaved changes in{' '}
            {dirty.map((k) => SECTIONS.find((s) => s.key === k)?.title ?? k).join(', ')}.
          </p>
          <div className="shopitems__actions">
            <button type="button" className="btn btn--sm" onClick={() => setAsking(false)}>
              Keep editing
            </button>
            <button type="button" className="btn btn--sm btn--danger" onClick={onClose}>
              Close without saving
            </button>
          </div>
        </div>
      )}
      <ShopItemEditor
        item={item}
        rooms={rooms}
        tenantId={tenantId}
        canAdmin={canAdmin}
        openSections={openSections}
        onDirtyChange={(keys) => {
          setDirty(keys)
          if (keys.length === 0) setAsking(false)
        }}
        onChanged={onChanged}
      />
    </Dialog>
  )
}

/**
 * The tray's contents: the item's chips, then eight sections that summarise
 * themselves. Exported so the suite can render it without the list.
 */
export function ShopItemEditor({
  item,
  rooms,
  tenantId,
  canAdmin,
  openSections,
  onDirtyChange,
  onChanged,
}: {
  item: ManagedItem
  rooms: StockRoom[]
  tenantId: string
  canAdmin: boolean
  openSections?: SectionKey[] | 'all'
  onDirtyChange?: (keys: SectionKey[]) => void
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [price, setPrice] = useState(String(item.priceStars))
  const [open, setOpen] = useState<SectionKey[]>(() =>
    openSections === 'all' ? ALL_SECTIONS : openSections ?? defaultOpenSections(item))
  const [dirty, setDirty] = useState<Partial<Record<SectionKey, boolean>>>({})
  const [detailsDirty, setDetailsDirty] = useState(false)

  // The price field follows the server after a save — the old row keyed its
  // state the same way, through a remount.
  useEffect(() => setPrice(String(item.priceStars)), [item.priceStars])

  const priceDirty = price !== String(item.priceStars)
  const anyDetails = detailsDirty || priceDirty
  const dirtyKeys = ALL_SECTIONS.filter((k) => (k === 'details' ? anyDetails : dirty[k]))
  const dirtySig = dirtyKeys.join(',')
  const reportUp = useRef(onDirtyChange)
  reportUp.current = onDirtyChange
  useEffect(() => {
    reportUp.current?.(dirtySig ? (dirtySig.split(',') as SectionKey[]) : [])
  }, [dirtySig])

  const mark = (k: SectionKey) => (d: boolean) =>
    setDirty((prev) => (Boolean(prev[k]) === d ? prev : { ...prev, [k]: d }))

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key)
    setError(null)
    try {
      await fn()
      onChanged()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  const toggle = (k: SectionKey) =>
    setOpen((o) => (o.includes(k) ? o.filter((x) => x !== k) : [...o, k]))
  const allOpen = open.length === ALL_SECTIONS.length

  const summary: Record<SectionKey, string> = {
    details: detailsSummary(item),
    options: optionsSummary(item),
    pictures: picturesSummary(item),
    ordering: orderingSummary(item),
    pledges: pledgesSummary(item),
    audience: audienceSummary(item),
    stock: stockSummary(item),
    orders: item.iMayEdit ? 'Count on demand · CSV for the supplier' : 'Whoever prices it can count them',
  }

  const notYours = (
    <p className="admin__hint">
      Only whoever prices this item can change it — a shop administrator for a
      group item, the owning store for a local one.
    </p>
  )

  const body: Record<SectionKey, React.ReactNode> = {
    details: (
      <>
        {/* 076 — THE WRITE PATH `create_shop_item` NEVER HAD A PARTNER FOR:
            name, description and owning room. */}
        {item.iMayEdit ? (
          <ItemDetails
            key={`${item.name}|${item.description ?? ''}|${item.ownerRoomId ?? ''}`}
            item={item}
            rooms={rooms}
            canAdmin={canAdmin}
            onError={setError}
            onChanged={onChanged}
            onDirty={setDetailsDirty}
          />
        ) : (
          item.description && <p className="shopitems__desc">{item.description}</p>
        )}
        {item.iMayEdit && (
          <div className="shopitems__controls">
            <label className="admin__field">
              <span>Price in stars</span>
              <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
            </label>
            <button
              type="button"
              className="btn btn--sm"
              disabled={busy === 'price' || !priceDirty}
              onClick={() => run('price', () => setItemPrice(item.itemId, Number(price)))}
            >
              {busy === 'price' ? 'Saving…' : 'Save price'}
            </button>
            <RowToggle
              label={`Whether ${item.name} is in the shop`}
              value={item.isActive ? 'listed' : 'delisted'}
              options={[
                { key: 'listed', text: 'In the shop', title: 'Employees can see and order it' },
                { key: 'delisted', text: 'Delisted', title: 'Hidden from the shop, kept here' },
              ]}
              busy={busy === 'active'}
              onPick={(k) => run('active', () => setItemActive(item.itemId, k === 'listed'))}
            />
          </div>
        )}
        {/* A re-price does not rewrite history: an order records what it
            charged. Said here because this is the control that makes people ask. */}
        <p className="admin__hint">
          Changing the price affects new orders only — an order keeps the price
          it was placed at.
        </p>
      </>
    ),
    options: item.iMayEdit ? (
      <ItemOptions
        // The server's options are in the key, so the fields remount when
        // somebody else changes them — useState initialised from a prop keeps
        // the first value it ever saw.
        key={item.axes.map((a) => `${a.label}:${a.options.map((o) => o.label).join(',')}`).join('|')}
        item={item}
        onError={setError}
        onChanged={onChanged}
        onDirty={mark('options')}
      />
    ) : notYours,
    pictures: <ItemPictures item={item} tenantId={tenantId} onError={setError} onChanged={onChanged} />,
    ordering: item.iMayEdit ? (
      <ItemOrdering
        key={`${item.maxPerPerson}-${item.allowsBackorder}-${item.ordersOpenAt}-${item.ordersCloseAt}-${item.backorderExceptions.map((x) => x.stockRoomId).join(',')}`}
        item={item}
        rooms={rooms}
        onError={setError}
        onChanged={onChanged}
        onDirty={mark('ordering')}
      />
    ) : notYours,
    pledges: item.iMayEdit ? (
      <ItemPledges
        key={item.pledges.map((p) => p.pledgeId).join(',')}
        item={item}
        onError={setError}
        onChanged={onChanged}
        onDirty={mark('pledges')}
      />
    ) : notYours,
    audience: <ItemAudience item={item} busy={busy} onRun={run} onError={setError} onChanged={onChanged} />,
    stock: <ItemStock item={item} rooms={rooms} />,
    orders: item.iMayEdit ? <ItemDemand item={item} onError={setError} /> : notYours,
  }

  return (
    <div className="shopitems__panel">
      <div className="tray__meta">
        <span className="shopitems__code">{item.code}</span>
        {statusChips(item).map((c) => (
          <span key={c.text} className={`tag shopitems__chip shopitems__chip--${c.tone}`}>{c.text}</span>
        ))}
        <button
          type="button"
          className="btn btn--link tray__all"
          onClick={() => setOpen(allOpen ? [] : ALL_SECTIONS)}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      {!item.iMayEdit && (
        <p className="admin__hint">
          This is somebody else's to change. A group item is priced by a shop
          administrator; a local one by the room that owns it.
        </p>
      )}

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {SECTIONS.map((s) => (
        <TraySection
          key={s.key}
          title={s.title}
          summary={summary[s.key]}
          open={open.includes(s.key)}
          dirty={dirtyKeys.includes(s.key)}
          onToggle={() => toggle(s.key)}
        >
          {body[s.key]}
        </TraySection>
      ))}
    </div>
  )
}

/**
 * WHERE IT SITS, per store — replacing the per-variant × per-store table, which
 * was the longest thing in the panel (dozens of rows for the 40-combination CYW
 * shirt) and the only one that could not be changed here. Counting happens on
 * the Stock tab, and the link goes there filtered to this item.
 */
function ItemStock({ item, rooms }: { item: ManagedItem; rooms: StockRoom[] }) {
  const stores = stockByStore(item, rooms)
  if (item.variants.length === 0) {
    return (
      <p className="empty">
        No combinations yet, so there is nowhere for stock to live. Stock hangs
        off the variant, never off the item.
      </p>
    )
  }
  return (
    <>
      {stores.length === 0 ? (
        <p className="admin__hint">Not on any shelf yet.</p>
      ) : (
        <table className="admin__table shopitems__table">
          <thead>
            <tr>
              <th scope="col">Store</th>
              <th scope="col">On hand</th>
              <th scope="col">Held</th>
              <th scope="col">Free</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((r) => (
              <tr key={r.stockRoomId}>
                <th scope="row">
                  {r.roomName}
                  {r.closed && <span className="pill pill--draft">Closed</span>}
                </th>
                <td>{r.onHand}</td>
                {/* Held is never typed anywhere. The order path writes it. */}
                <td>{r.held}</td>
                <td>{r.free}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* WHERE THE NUMBER IS CHANGED, said on the screen that does not change
          it (REC-31's lesson). A plain anchor rather than <Link>: this renders
          under the suite with no router. */}
      <p className="admin__hint">
        Read-only here. Counting a shelf is a different act from editing the
        item, so it happens on <strong>Stock</strong>, in the room you are
        standing in — which is also where a reorder level is set.{' '}
        <a href={`#/admin/shop?item=${encodeURIComponent(item.itemId)}`}>Open on the Stock tab</a>
      </p>
    </>
  )
}

/**
 * The item's own facts: what it is called, what it says, and which store owns
 * it. Behind a pencil rather than open, which is `ProfileEditor`'s shape — an
 * input sitting open is an invitation to type over a name ~900 people see.
 *
 * **`code` IS SHOWN AND IS NOT AN INPUT** (067, and `EditRoom` draws the same
 * line): orders, stock and images hang off it, so an identifier you may rewrite
 * is one that stops identifying. **THE OWNING ROOM IS SHOP.ADMIN ONLY** — it
 * decides who may price the item (065), so handing it to another room is not
 * the same act as running a shelf (021). The control is hidden for anybody
 * else rather than offered and refused.
 *
 * A REPLACE with no defaults on the server (049), so the form sends every field
 * and clearing the description clears it.
 */
function ItemDetails({
  item,
  rooms,
  canAdmin,
  onError,
  onChanged,
  onDirty,
}: {
  item: ManagedItem
  rooms: StockRoom[]
  canAdmin: boolean
  onError: (m: string | null) => void
  onChanged: () => void
  onDirty?: (d: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(item.name)
  const [description, setDescription] = useState(item.description ?? '')
  const [owner, setOwner] = useState(item.ownerRoomId ?? '')
  const [busy, setBusy] = useState(false)

  const ready = name.trim() !== ''
  useReportDirty(
    open && (name !== item.name || description !== (item.description ?? '') || owner !== (item.ownerRoomId ?? '')),
    onDirty,
  )

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError(null)
    try {
      await editShopItem({
        itemId: item.itemId,
        name: name.trim(),
        description: description.trim() || null,
        ownerRoomId: owner || null,
      })
      setOpen(false)
      onChanged()
    } catch (err) {
      onError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <div className="shopitems__details">
        {item.description
          ? <p className="shopitems__desc">{item.description}</p>
          : <p className="admin__hint">No description. Swag is bought by looking at it and reading one line.</p>}
        <button type="button" className="btn btn--sm" onClick={() => setOpen(true)}>
          Edit details
        </button>
      </div>
    )
  }

  return (
    <form className="shopitems__new" onSubmit={save}>
      <p className="admin__hint">
        Short code <code>{item.code}</code> — fixed. Orders, stock and pictures
        hang off it, so a code that can be rewritten is one that stops
        identifying the item.
      </p>
      <div className="admin__fields">
        <label className="admin__field">
          <span>Name</span>
          <input value={name} disabled={busy} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="admin__field shopitems__wide">
          <span>Description</span>
          <input
            value={description}
            disabled={busy}
            placeholder="Navy, embroidered Conetec mark"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        {canAdmin && (
          <label className="admin__field">
            <span>Priced by</span>
            <select value={owner} disabled={busy} onChange={(e) => setOwner(e.target.value)}>
              <option value="">The whole company</option>
              {rooms.filter((r) => r.isActive).map((r) => (
                <option key={r.id} value={r.id}>{r.name} only</option>
              ))}
            </select>
          </label>
        )}
      </div>
      <p className="admin__hint">
        Every field is sent, so clearing the description empties it.
        {canAdmin
          ? ' Changing who prices it does not move any stock — that is Move, on the Stock tab.'
          : ' Who prices it is a shop administrator’s call, so it is not offered here.'}
      </p>
      <div className="shopitems__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={!ready || busy}>
          {busy ? 'Saving…' : 'Save details'}
        </button>
        <button type="button" className="btn btn--sm" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  )
}

/**
 * `datetime-local` wants the reader's LOCAL wall time as `YYYY-MM-DDTHH:mm`.
 * Before 129 this screen sliced the ISO string, which is UTC: a close date set
 * at 17:00 in Burnaby read back as 00:00 the next day and moved by seven hours
 * on every save that touched anything else.
 */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Where an item may be ordered with an empty shelf — the three answers. */
type Reach = 'stock' | 'every' | 'only'

function initialReach(item: ManagedItem): Reach {
  if (item.allowsBackorder) return 'every'
  return item.backorderExceptions.some((x) => x.allowsBackorder) ? 'only' : 'stock'
}

/**
 * Every ordering fact about an item, saved in ONE call (069, reshaped by 129).
 *
 * ── THERE IS NO "PRE-ORDER" TICKBOX, AND ITS ABSENCE IS THE DECISION ────────
 *
 * A pre-order is backorder PLUS an orders window (David, 14 Sep; kept by 129).
 * Each fact is useful alone — a window with no backorder is a limited window
 * on stock we hold; backorder with no window is an ordinary item you will
 * wait for. The screen NAMES the combination instead of storing it.
 *
 * ── "ALMOST ANY LOCATION" IS A DEFAULT AND ITS EXCEPTIONS (129) ─────────────
 *
 * Three answers, not a checkbox per store: "in stock only", "every store
 * except…", "only these stores". The middle one is the CYW shirt. It is stored
 * as the item default plus the stores that differ, so a store opened next year
 * joins an "every store except" item by itself and stays out of an "only
 * these" one by itself — a stored list of stores could not tell those apart.
 * Saving REPLACES every per-store override on the item, a room manager's own
 * included: this list is the item's answer.
 *
 * All five facts go together because the server is a REPLACE with no defaults
 * (049): omitting one silently clears it.
 */
function ItemOrdering({
  item,
  rooms,
  onError,
  onChanged,
  onDirty,
}: {
  item: ManagedItem
  rooms: StockRoom[]
  onError: (m: string | null) => void
  onChanged: () => void
  onDirty?: (d: boolean) => void
}) {
  const [limit, setLimit] = useState(item.maxPerPerson?.toString() ?? '')
  const [reach, setReach] = useState<Reach>(initialReach(item))
  // The stores that DIFFER from the default: excluded under "every", included
  // under "only". One set, read through `included` below, so the chips cannot
  // disagree with what is sent.
  const initialPicked = item.backorderExceptions
    .filter((x) => x.allowsBackorder === (initialReach(item) === 'only'))
    .map((x) => x.stockRoomId)
  const [picked, setPicked] = useState<string[]>(initialPicked)
  const [opens, setOpens] = useState(toLocalInput(item.ordersOpenAt))
  const [closes, setCloses] = useState(toLocalInput(item.ordersCloseAt))
  const [busy, setBusy] = useState(false)

  const activeRooms = rooms.filter((r) => r.isActive)
  const included = (id: string) => (reach === 'every' ? !picked.includes(id) : picked.includes(id))
  const takingCount = reach === 'stock' ? 0 : activeRooms.filter((r) => included(r.id)).length

  const parsedLimit = limit.trim() === '' ? null : Number.parseInt(limit, 10)
  // Junk and zero are both refused, and NaN is the one that matters: without
  // the isFinite check a typed "abc" reads as a change and enables the save,
  // which then sends NaN and fails at the constraint (046's lesson about a
  // control that looks ready and is not).
  const limitBad = parsedLimit !== null && (!Number.isFinite(parsedLimit) || parsedLimit < 1)
  const windowBad = opens !== '' && closes !== '' && new Date(opens) >= new Date(closes)

  const samePicked =
    picked.length === initialPicked.length && picked.every((id) => initialPicked.includes(id))
  const changed =
    (!limitBad && (item.maxPerPerson ?? null) !== parsedLimit) ||
    reach !== initialReach(item) ||
    !samePicked ||
    toLocalInput(item.ordersOpenAt) !== opens ||
    toLocalInput(item.ordersCloseAt) !== closes
  useReportDirty(changed, onDirty)

  function pickReach(next: Reach) {
    if (next === reach) return
    // A switch of answer starts the exceptions again: "every store except
    // Paris" and "only Paris" are opposite sentences about the same chip.
    setReach(next)
    setPicked([])
  }

  async function save() {
    setBusy(true)
    onError(null)
    try {
      await setItemOrdering(item.itemId, {
        maxPerPerson: parsedLimit,
        allowsBackorder: reach === 'every',
        backorderExceptions: reach === 'stock' ? [] : picked,
        ordersOpenAt: opens === '' ? null : new Date(opens).toISOString(),
        ordersCloseAt: closes === '' ? null : new Date(closes).toISOString(),
      })
      onChanged()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const reachOptions: { key: Reach; text: string; title: string }[] = [
    { key: 'stock', text: 'No, shelf stock only', title: 'People can order only what is on a store\'s shelf' },
    { key: 'every', text: 'Yes, at every store except…', title: 'Any store takes an order with no stock — untick the ones that should not' },
    { key: 'only', text: 'Yes, only at these stores', title: 'Only the stores ticked below take an order with no stock' },
  ]

  return (
    <div className="shopitems__options">
      <h4 className="shopitems__subhead">Ordering</h4>

      <div className="shopitems__controls">
        <label className="admin__field">
          <span>Most per person</span>
          <input
            className="shopitems__count"
            inputMode="numeric"
            value={limit}
            disabled={busy}
            placeholder="No limit"
            onChange={(e) => setLimit(e.target.value)}
          />
        </label>

        <label className="admin__field">
          <span>Orders open</span>
          <input
            type="datetime-local"
            value={opens}
            disabled={busy}
            onChange={(e) => setOpens(e.target.value)}
          />
        </label>

        <label className="admin__field">
          <span>Orders close</span>
          <input
            type="datetime-local"
            value={closes}
            disabled={busy}
            onChange={(e) => setCloses(e.target.value)}
          />
        </label>
      </div>

      {/* David, 4 Oct: "When the shelf is empty" did not say what was being
          decided. The question is whether a store takes an order it cannot
          fill yet; a store left out still sells what is on its own shelf. */}
      <p className="shop__axislabel" id={`reach-${item.itemId}`}>Take orders when there's no stock?</p>
      <div className="chips" role="group" aria-labelledby={`reach-${item.itemId}`}>
        {reachOptions.map((o) => (
          <button
            key={o.key}
            type="button"
            className={`chip chip--sm${reach === o.key ? ' chip--on' : ''}`}
            aria-pressed={reach === o.key}
            title={o.title}
            disabled={busy}
            onClick={() => pickReach(o.key)}
          >
            {o.text}
          </button>
        ))}
      </div>

      {reach !== 'stock' && (
        <>
          <p className="admin__hint">
            {reach === 'every'
              ? 'Untick a store to leave it out — it still sells what is on its own shelf. A store added later takes orders too.'
              : 'Tick the stores that take orders. The rest sell only what is on their shelf, and a store added later does not take orders.'}
            {' '}Takes orders at {takingCount} of {activeRooms.length}.
          </p>
          <div className="chips" role="group" aria-label="Stores that take orders with no stock">
            {activeRooms.map((r) => {
              const on = included(r.id)
              return (
                <button
                  key={r.id}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  className={`chip chip--sm${on ? ' chip--on' : ''}`}
                  disabled={busy}
                  title={`${r.city}, ${r.country}`}
                  onClick={() =>
                    setPicked((p) => (p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id]))
                  }
                >
                  {on ? '✓ ' : ''}{r.name}
                </button>
              )
            })}
          </div>
        </>
      )}

      {limitBad && (
        <p className="shopitems__refusal" role="alert">
          A limit below one is a delisting, not a limit — leave it empty for no
          limit, or take the item off the shop floor.
        </p>
      )}
      {windowBad && (
        <p className="shopitems__refusal" role="alert">
          Orders have to open before they close.
        </p>
      )}

      <p className="admin__hint">
        {reach !== 'stock' && closes !== ''
          ? 'Orders with no stock plus a closing date is a pre-order: the store says "Pre-order until …", each order waits, and stock arriving fills them oldest first, per store.'
          : reach !== 'stock'
            ? 'Somebody can order this when the shelf is empty and join a queue, oldest first, per store.'
            : closes !== ''
              ? 'A limited window on stock we actually hold — orders stop on that date whatever is left.'
              : 'Orderable while there is stock, and refused when there is not.'}
        {opens !== '' && ' Before it opens, the item is listed with its opening date.'}
      </p>

      <button
        type="button"
        className="btn btn--sm"
        disabled={busy || !changed || limitBad || windowBad}
        onClick={save}
      >
        {busy ? 'Saving…' : changed ? 'Save ordering rules' : 'Saved'}
      </button>
    </div>
  )
}

/**
 * The options per axis — what David asked for: "what varies, and then the
 * options for those variations".
 *
 * ONE FIELD PER AXIS, comma separated, and the database makes the cross
 * product. The alternative was a row of "add a variant" boxes, which is what
 * 065 had: adding a jacket in two colours and five sizes meant pressing Add ten
 * times and getting the combinations right by hand.
 *
 * It is a REPLACE, and the screen says so before the press — anything removed
 * from these lists stops being sold. The database deactivates rather than
 * deletes and refuses outright if stock is promised to an order, so the worst
 * case here is a refusal rather than a loss.
 */
function ItemOptions({
  item,
  onError,
  onChanged,
  onDirty,
}: {
  item: ManagedItem
  onError: (m: string | null) => void
  onChanged: () => void
  onDirty?: (d: boolean) => void
}) {
  const stored = toDrafts(item)
  const [draft, setDraft] = useState<AxisDraft[]>(stored)
  const [busy, setBusy] = useState(false)

  // Compared as text so the same shape typed differently ("Navy, Grey" against
  // "Navy,Grey") reads as unchanged and the button offers no redundant write.
  const asText = (a: AxisDraft[]) =>
    a.map((x) => `${x.kind}:${x.label}:${x.options.join(',')}`).join('|')
  const changedShape = asText(draft) !== asText(stored)
  const ready = axesReady(draft)
  useReportDirty(changedShape, onDirty)

  async function save() {
    setBusy(true)
    onError(null)
    try {
      await setItemCatalogue(item.itemId, draft)
      onChanged()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shopitems__options">
      <h4 className="shopitems__subhead">What it comes in</h4>

      <AxisEditor axes={draft} onChange={setDraft} disabled={busy} />

      {/* CHANGING THE SHAPE IS NOW ONE CALL AND ONE CONTROL. 067 needed a
          separate "change what varies" escape hatch because declaring the axes
          and generating the combinations were two functions, so an item could
          sit between them. There is no between now — and the database refuses
          the whole call if stock is promised to an order, so the refusal
          arrives before anything is half-applied. */}
      <p className="admin__hint">
        Removing an option stops that combination being sold and keeps whatever
        is on the shelf. Adding one back brings its stock with it.
      </p>

      <button
        type="button"
        className="btn btn--sm"
        disabled={busy || !changedShape || !ready}
        onClick={save}
      >
        {busy ? 'Saving…' : changedShape ? 'Save what it comes in' : 'Saved'}
      </button>
    </div>
  )
}


/**
 * EMPTY MEANS EVERYONE, and it is said in words.
 *
 * An empty list reads as "for nobody", which is the opposite of what it means —
 * and a manager reading it that way would start adding rows to fix something
 * that is not broken. The first row added is the NARROWING, so it is the one
 * worth a sentence.
 */
function ItemAudience({
  item,
  busy,
  onRun,
  onError,
  onChanged,
}: {
  item: ManagedItem
  busy: string | null
  onRun: (key: string, fn: () => Promise<unknown>) => Promise<void>
  onError: (m: string | null) => void
  onChanged: () => void
}) {
  return (
    <div className="shopitems__audience">
      <h4 className="shopitems__subhead">Who it is for</h4>
      {item.audience.length === 0 ? (
        <p className="admin__hint">
          Everyone. Adding the first name or business unit below narrows it —
          after that, only the people named can order it.
        </p>
      ) : (
        <ul className="chips">
          {item.audience.map((a) => (
            <li key={a.eligibilityId}>
              <span className="chip chip--value">
                {/* Null when RLS hides the target. The row is still listed,
                    because "there is a restriction whose detail you cannot see"
                    is true and useful — claiming the item is unrestricted would
                    not be. */}
                {a.label ?? 'Someone you cannot see'}
                {item.iMayEdit && (
                  <button
                    type="button"
                    className="chip__x"
                    aria-label={`Remove ${a.label ?? 'this restriction'}`}
                    disabled={busy === `aud-${a.eligibilityId}`}
                    onClick={() =>
                      onRun(`aud-${a.eligibilityId}`, () => removeItemAudience(a.eligibilityId))
                    }
                  >
                    ×
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* THE CONTROL THE COPY ABOVE HAS BEEN POINTING AT SINCE 066.
          `add_shop_item_audience` was built that day and nothing ever called
          it, so the sentence said "adding the first name or business unit
          below" with nothing below it. Tenth instance of a write path with no
          surface — and the loudest, because the screen described the missing
          control rather than merely omitting it. */}
      {item.iMayEdit && (
        <AudienceAdder item={item} onError={onError} onChanged={onChanged} />
      )}
    </div>
  )
}

/**
 * Narrowing an item to a unit or a person.
 *
 * ── TWO CONTROLS, BECAUSE THEY ARE TWO DIFFERENT SHAPES ─────────────────────
 *
 * An org unit is a LIST — 69 of them, already loaded for the admin filters, and
 * the shape of the company is not personal data. A person is a SEARCH: there
 * are 932, and 043 refused to hand the roster to every caller precisely so it
 * never becomes a pageable table. `mentionable_people` is the search that
 * already exists for the @ menu and returns a handful per query, so this uses
 * it rather than inventing a second person lookup (062c's warning).
 *
 * ── THE FIRST ROW ADDED IS THE NARROWING, AND IT IS SAID OUT LOUD ───────────
 *
 * Empty means everyone (065/066). So adding the first row is the consequential
 * act and the one after it is routine — which is the opposite of how a list of
 * chips reads. The warning sits above the control while the list is empty and
 * disappears once it is not.
 */
function AudienceAdder({
  item,
  onError,
  onChanged,
}: {
  item: ManagedItem
  onError: (m: string | null) => void
  onChanged: () => void
}) {
  const [units, setUnits] = useState<OrgUnitOption[]>([])
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<MentionablePerson[]>([])
  /** What matched, from the server (088). REC-54. */
  const [foundTotal, setFoundTotal] = useState(0)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    loadOrgUnits()
      .then((u) => live && setUnits(u))
      .catch(() => live && setUnits([]))
    return () => { live = false }
  }, [])

  // Searched on every keystroke past one character, the same as the @ menu.
  useEffect(() => {
    let live = true
    if (query.trim().length < 2) {
      setFound([])
      return
    }
    searchMentionable(query.trim())
      .then((page) => {
        if (!live) return
        setFound(page.people)
        // REC-54. The audience adder shares `mentionable_people` and truncated
        // at six in silence like the rest. Here the cost is an item narrowed to
        // the wrong person because the right one was never on the list.
        setFoundTotal(page.total)
      })
      .catch(() => {
        if (!live) return
        setFound([])
        setFoundTotal(0)
      })
    return () => { live = false }
  }, [query])

  const already = new Set(
    item.audience.flatMap((a) => [a.orgUnitId, a.personId].filter(Boolean) as string[]),
  )

  async function add(target: { orgUnitId?: string; personId?: string }) {
    setBusy(true)
    onError(null)
    try {
      await addItemAudience(item.itemId, target)
      setQuery('')
      setFound([])
      onChanged()
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shopitems__adder">
      {item.audience.length === 0 && (
        <p className="admin__hint">
          <strong>Adding the first one narrows it.</strong> Until then everybody
          can order this.
        </p>
      )}

      <div className="shopitems__controls">
        <Picker
          label="Part of the company"
          anyLabel="Add a business unit or department"
          searchPlaceholder="Search units"
          options={units
            .filter((u) => !already.has(u.orgUnitId))
            .map((u) => ({ value: u.orgUnitId, label: u.name, depth: u.depth, hint: u.path }))}
          // Nothing is ever "selected" here: the chips above are the state, and
          // this is an ADD control. FilterChips' split, one screen over.
          selected={[]}
          onChange={(next) => {
            const id = next[0]
            if (id && !busy) void add({ orgUnitId: id })
          }}
        />

        <label className="admin__field">
          <span>Or a person</span>
          <input
            value={query}
            disabled={busy}
            placeholder="Start typing a name"
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      {found.length > 0 && (
        <ul className="shopitems__found">
          {found
            .filter((p) => !already.has(p.personId))
            .map((p) => (
              <li key={p.personId}>
                <button
                  type="button"
                  className="btn btn--xs"
                  disabled={busy}
                  onClick={() => void add({ personId: p.personId })}
                >
                  Add {p.displayName}
                </button>
              </li>
            ))}
        </ul>
      )}
      {query.trim().length >= 2 && found.length === 0 && (
        <p className="admin__hint">Nobody by that name.</p>
      )}
      {/* REC-54. `shown` is what the SERVER returned, not what survived the
          already-added filter above: the sentence is about the search, and
          somebody already on the list was still found. Counting the buttons
          would make "showing 5 of 20" mean two different things on two
          screens. */}
      <PickerFoot shown={found.length} total={foundTotal} hasQuery />
    </div>
  )
}

/**
 * A new item — SHORT, then into the tray (REC-65).
 *
 * Only what `create_shop_item` needs: a name, its code, a price and who prices
 * it. The options, pictures, ordering rules and the rest are built in the tray
 * the moment it exists, with "What it comes in" already open — which is how the
 * server has always worked (the item exists before anything hangs off it), and
 * it replaces a long modal that asked for all of it up front.
 *
 * A GROUP item needs shop.admin; a LOCAL one needs the room that will own it.
 * That is the whole ownership rule and the form states it rather than
 * discovering it in a refusal — `owner_room_id` is null or set, never a
 * boolean, because two price columns would need a rule for what happens when
 * both are filled in.
 */
export function NewShopItem({
  tenantId,
  rooms,
  canAdmin,
  onCreated,
  onCancel,
}: {
  tenantId: string
  rooms: StockRoom[]
  canAdmin: boolean
  onCreated: (itemId: string) => void
  onCancel: () => void
}) {
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [owner, setOwner] = useState<string>(canAdmin ? '' : (rooms.find((r) => r.iManage)?.id ?? ''))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const myRooms = rooms.filter((r) => r.iManage && r.isActive)
  const effectiveCode = codeTouched ? code : slugifyCode(name)
  const codeIssue = name.trim() === '' ? null : codeProblem(effectiveCode)

  const ready = name.trim() && codeIssue === null && price.trim() !== '' && Number(price) >= 0

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const itemId = await createShopItem({
        tenantId,
        code: effectiveCode,
        name: name.trim(),
        priceStars: Number(price),
        ownerRoomId: owner || null,
      })
      onCreated(itemId)
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="shopitems__new" onSubmit={submit}>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      <div className="admin__fields">
        {/* NAME FIRST. It is the thing the person knows; the code follows from
            it. The old order asked for a machine key before the item had a
            name, which is also how it ended up with a space in it. */}
        <label className="admin__field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Softshell jacket" />
        </label>
        <label className="admin__field">
          <span>Code</span>
          <input
            value={effectiveCode}
            onChange={(e) => {
              setCodeTouched(true)
              setCode(e.target.value)
            }}
            placeholder="softshell-jacket"
            aria-invalid={codeIssue !== null || undefined}
          />
        </label>
        <label className="admin__field">
          <span>Price in stars</span>
          <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
        <label className="admin__field">
          <span>Priced by</span>
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            {canAdmin && <option value="">The whole company</option>}
            {myRooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} only
              </option>
            ))}
          </select>
        </label>
      </div>

      {codeIssue ? (
        <p className="shopitems__refusal" role="alert">
          {codeIssue}
        </p>
      ) : (
        <p className="admin__hint">
          The code is filled in from the name and is how the item is referred to
          internally. Change it if you want; it cannot contain spaces or capitals,
          and it is fixed once the item exists. Next you choose what it comes in.
        </p>
      )}

      <div className="shopitems__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={!ready || busy}>
          {busy ? 'Creating…' : 'Create and continue'}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}
